/**
 * Google Analytics 4 (GA4) analytics utility
 * Provides typed helpers for tracking custom events across the site.
 *
 * The GA4 measurement ID is read from the VITE_GA4_MEASUREMENT_ID env var.
 * If the variable is not set or gtag is not loaded, calls are safely no-ops.
 */

// Extend Window to include gtag / dataLayer
declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

/* ------------------------------------------------------------------ */
/*  Core helpers                                                       */
/* ------------------------------------------------------------------ */

/** Send a GA4 event (no-op when gtag is unavailable). */
function trackEvent(eventName: string, params?: Record<string, unknown>) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, params);
  }
}

/** Set a GA4 user property. */
function setUserProperty(key: string, value: string | number | boolean) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('set', 'user_properties', { [key]: value });
  }
}

/* ------------------------------------------------------------------ */
/*  Auth events                                                        */
/* ------------------------------------------------------------------ */

export function trackSignupStart(provider: 'google' | 'linkedin') {
  trackEvent('signup_start', { method: provider });
}

export function trackSignupComplete(provider: 'google' | 'linkedin', isNewUser: boolean) {
  trackEvent('signup_complete', { method: provider, is_new_user: isNewUser });
  if (isNewUser) {
    // GA4 recommended event
    trackEvent('sign_up', { method: provider });
  } else {
    trackEvent('login', { method: provider });
  }
}

export function trackSignupError(provider: 'google' | 'linkedin', error: string) {
  trackEvent('signup_error', { method: provider, error_message: error.slice(0, 100) });
}

/* ------------------------------------------------------------------ */
/*  Pricing / plan selection events                                    */
/* ------------------------------------------------------------------ */

export function trackPlanSelected(planName: string, billing: 'monthly' | 'yearly', price: number) {
  trackEvent('plan_selected', { plan_name: planName, billing_interval: billing, value: price, currency: 'USD' });
}

export function trackBillingToggle(isAnnual: boolean) {
  trackEvent('billing_toggle', { billing_interval: isAnnual ? 'yearly' : 'monthly' });
}

/* ------------------------------------------------------------------ */
/*  Checkout events                                                    */
/* ------------------------------------------------------------------ */

export function trackCheckoutStart(planName: string, billing: string, type: string) {
  trackEvent('begin_checkout', { plan_name: planName, billing_interval: billing, checkout_type: type, currency: 'USD' });
}

export function trackCheckoutComplete(planName: string, licenseKey?: string) {
  trackEvent('purchase', { plan_name: planName, currency: 'USD' });
  trackEvent('checkout_complete', { plan_name: planName });
}

/* ------------------------------------------------------------------ */
/*  Extension install click                                            */
/* ------------------------------------------------------------------ */

export function trackExtensionInstallClick(source: string) {
  trackEvent('extension_install_click', { source });
}

/* ------------------------------------------------------------------ */
/*  Page views (SPA — fired manually on route change)                  */
/* ------------------------------------------------------------------ */

export function trackPageView(path: string, title?: string) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', import.meta.env.VITE_GA4_MEASUREMENT_ID, {
      page_path: path,
      page_title: title,
    });
  }
}

/* ------------------------------------------------------------------ */
/*  User identification (after auth)                                   */
/* ------------------------------------------------------------------ */

export function identifyUser(userId: string, plan?: string) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', import.meta.env.VITE_GA4_MEASUREMENT_ID, { user_id: userId });
  }
  if (plan) setUserProperty('plan', plan);
}

/* ------------------------------------------------------------------ */
/*  UTM helpers (used by utm module, re-exported for convenience)      */
/* ------------------------------------------------------------------ */

export function trackUtmLanding(params: Record<string, string>) {
  trackEvent('utm_landing', params);
}

/* ------------------------------------------------------------------ */
/*  Referral events                                                    */
/* ------------------------------------------------------------------ */

export function trackReferralLinkCopied() {
  trackEvent('referral_link_copied');
}

export function trackReferralSignup(referrerCode: string) {
  trackEvent('referral_signup', { referrer_code: referrerCode });
}

export default {
  trackEvent,
  trackSignupStart,
  trackSignupComplete,
  trackSignupError,
  trackPlanSelected,
  trackBillingToggle,
  trackCheckoutStart,
  trackCheckoutComplete,
  trackExtensionInstallClick,
  trackPageView,
  identifyUser,
  trackUtmLanding,
  trackReferralLinkCopied,
  trackReferralSignup,
};
